import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  X,
  ShoppingCart,
  User,
  Package,
  Truck,
  CreditCard,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Loader2,
  Search,
  Filter,
  Minus,
  Plus,
  AlertCircle,
  MapPin,
  DollarSign,
  ShoppingBag
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Card, CardContent } from './ui/card';
// Removed Dialog import to match OrderDetailsModal structure
import { getActiveShippingTiers, ShippingTier, fetchDhlExpressRateForAdmin, calculateShippingCostForAdmin } from '../services/shippingService';
import { searchProducts } from '../services/productService';
import { searchCustomers } from '../services/customerService';
import { currencyService, Currency, ExchangeRates } from '../services/currencyService';
import { validateDiscountCode, DiscountCode } from '../services/discountService';
import { createAdminOrder, validateOrderData, CreateOrderData } from '../services/adminOrderService';
import { Product, Customer } from '../types';

// Types for the order creation process
export interface OrderItem {
  id: string;
  name: string;
  price: number;
  onSalePrice?: number;
  onSale?: boolean;
  image: string;
  quantity: number;
  listingQuantity?: number;
}

export interface SelectedCustomer extends Customer {
  selectedAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  selectedAddressId?: string; // Track which address is selected
}

export interface DiscountInfo {
  code: string;
  amount: number;
  type: 'percentage' | 'fixed' | 'free_shipping';
}

// Remove duplicate type definitions - now imported from currencyService

interface CreateOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOrderCreated: () => void;
}

const steps = [
  { id: 'customer', title: 'Customer Info', icon: User, description: 'Customer details and address' },
  { id: 'items', title: 'Add Items', icon: Package, description: 'Select products for the order' },
  { id: 'shipping', title: 'Shipping', icon: Truck, description: 'Shipping options and processing' },
  { id: 'payment', title: 'Currency & Discounts', icon: CreditCard, description: 'Currency and discount codes' },
  { id: 'summary', title: 'Review & Create', icon: CheckCircle, description: 'Order summary and creation' }
];

// Customer Selection Step Component
interface CustomerSelectionStepProps {
  selectedCustomer: SelectedCustomer | null;
  onCustomerChange: (customer: SelectedCustomer | null) => void;
  validationErrors?: Record<string, string>;
}

function CustomerSelectionStep({ selectedCustomer, onCustomerChange, validationErrors }: CustomerSelectionStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);

  // Search customers
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      searchCustomersDebounced(searchQuery.trim());
    } else {
      setCustomers([]);
    }
  }, [searchQuery]);

  const searchCustomersDebounced = useCallback(async (query: string) => {
    try {
      setLoading(true);
      const result = await searchCustomers(query);
      setCustomers(result.customers);
    } catch (error) {
      console.error('Failed to search customers:', error);
      toast.error('Failed to search customers');
    } finally {
      setLoading(false);
    }
  }, []);

  // Convert Address to selectedAddress format
  const convertAddressToSelectedFormat = (address: any) => {
    if (!address) return null;
    
    // Handle both Address type (with zipCode) and legacy address type (with postal_code)
    return {
      line1: address.line1 || '',
      line2: address.line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.zipCode || address.postal_code || '',
      country: address.country || 'US'
    };
  };

  const handleSelectCustomer = (customer: Customer) => {
    // Find default address or use the first address
    const defaultAddress = customer.addresses?.find(addr => addr.isDefault) || customer.addresses?.[0];
    
    let selectedAddress = null;
    let selectedAddressId = null;
    
    if (defaultAddress) {
      selectedAddress = convertAddressToSelectedFormat(defaultAddress);
      selectedAddressId = defaultAddress.id;
    } else if (customer.address) {
      // Use legacy address format if available
      selectedAddress = convertAddressToSelectedFormat(customer.address);
      selectedAddressId = 'legacy';
    }

    const selectedCustomer: SelectedCustomer = {
      ...customer,
      selectedAddress,
      selectedAddressId
    };
    
    onCustomerChange(selectedCustomer);
  };

  // Handle address selection change
  const handleAddressChange = (addressId: string) => {
    if (!selectedCustomer) return;

    let selectedAddress = null;
    
    if (addressId === 'legacy' && selectedCustomer.address) {
      selectedAddress = convertAddressToSelectedFormat(selectedCustomer.address);
    } else {
      const address = selectedCustomer.addresses?.find(addr => addr.id === addressId);
      if (address) {
        selectedAddress = convertAddressToSelectedFormat(address);
      }
    }

    if (selectedAddress) {
      const updatedCustomer: SelectedCustomer = {
        ...selectedCustomer,
        selectedAddress,
        selectedAddressId: addressId
      };
      
      onCustomerChange(updatedCustomer);
    }
  };

  const handleClearCustomer = () => {
    onCustomerChange(null);
    setSearchQuery('');
    setCustomers([]);
  };

  return (
    <div className="space-y-6">
      {/* Selected Customer Summary */}
      {selectedCustomer && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{selectedCustomer.name}</h4>
                <p className="text-sm text-gray-600">{selectedCustomer.email}</p>
              {selectedCustomer.phone ? (
                <p className="text-sm text-gray-500">{selectedCustomer.phone}</p>
              ) : (
                <p className="text-sm text-orange-600">⚠️ No phone number on file</p>
              )}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearCustomer}
              className="text-red-600 border-red-300 hover:bg-red-50"
            >
              <X className="w-4 h-4 mr-1" />
              Change Customer
            </Button>
          </div>
          
          <div className="mt-4 pt-4 border-t border-blue-200">
            <h5 className="font-medium text-gray-900 mb-3">Shipping Address</h5>

            {/* Address Selection Dropdown */}
            {(selectedCustomer.addresses?.length > 1 || (selectedCustomer.addresses?.length > 0 && selectedCustomer.address)) && (
              <div className="mb-3">
                <Label htmlFor="address-select" className="text-sm font-medium text-gray-700">
                  Select Address:
                </Label>
                <select
                  id="address-select"
                  value={selectedCustomer.selectedAddressId || ''}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  {selectedCustomer.addresses?.map((address) => (
                    <option key={address.id} value={address.id}>
                      {address.label || 'Address'} {address.isDefault ? '(Default)' : ''} - {address.line1}, {address.city}
                    </option>
                  ))}
                  {selectedCustomer.address && (
                    <option value="legacy">
                      Legacy Address - {selectedCustomer.address.line1}, {selectedCustomer.address.city}
                    </option>
                  )}
                </select>
              </div>
            )}

            {/* Selected Address Display */}
            {selectedCustomer.selectedAddress ? (
              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                <p className="font-medium">{selectedCustomer.selectedAddress.line1}</p>
                {selectedCustomer.selectedAddress.line2 && (
                  <p>{selectedCustomer.selectedAddress.line2}</p>
                )}
                <p>
                  {selectedCustomer.selectedAddress.city}, {selectedCustomer.selectedAddress.state} {selectedCustomer.selectedAddress.postal_code}
                </p>
                <p>{selectedCustomer.selectedAddress.country}</p>
                {(() => {
                  // Get phone number from the selected address
                  let phoneNumber = null;
                  if (selectedCustomer.selectedAddressId === 'legacy') {
                    phoneNumber = selectedCustomer.phone;
                  } else {
                    const selectedAddr = selectedCustomer.addresses?.find(addr => addr.id === selectedCustomer.selectedAddressId);
                    phoneNumber = selectedAddr?.phoneNumber || selectedCustomer.phone;
                  }
                  
                  return phoneNumber ? (
                    <p className="text-green-600 mt-1">📞 {phoneNumber}</p>
                  ) : (
                    <p className="text-orange-600 mt-1">⚠️ No phone number available</p>
                  );
                })()}
              </div>
            ) : (
              <div className="text-sm text-red-600">
                <p>⚠️ No shipping address available for this customer</p>
                <p className="text-xs mt-1">This customer will need to have address information added before an order can be created.</p>
              </div>
            )}
          </div>
          
          <div className="mt-4 pt-4 border-t border-blue-200">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Orders:</span>
                <p className="font-medium">{selectedCustomer.orderHistory?.length || 0}</p>
              </div>
              <div>
                <span className="text-gray-500">Total Spent:</span>
                <p className="font-medium">${selectedCustomer.totalSpent?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <span className="text-gray-500">Last Order:</span>
                <p className="font-medium">
                  {selectedCustomer.lastOrderDate 
                    ? new Date(selectedCustomer.lastOrderDate).toLocaleDateString()
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors */}
      {validationErrors && Object.keys(validationErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <h5 className="font-medium text-red-900">Please fix the following issues:</h5>
          </div>
          <ul className="text-sm text-red-700 space-y-1">
            {Object.entries(validationErrors).map(([field, error]) => (
              <li key={field}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Customer Search */}
      {!selectedCustomer && (
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search customers by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
              </div>
            )}
          </div>

          {/* Search Results */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {searchQuery.trim().length >= 2 && !loading && customers.length === 0 && (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No customers found</h3>
                <p className="text-gray-600">
                  Try adjusting your search terms or check the spelling.
                </p>
              </div>
            )}

            {customers.map((customer) => {
              const defaultAddress = customer.addresses?.find(addr => addr.isDefault) || customer.addresses?.[0];
              const address = defaultAddress || customer.address;
              
              return (
                <motion.div
                  key={customer.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="transition-all duration-200 hover:shadow-md cursor-pointer border-gray-200 hover:border-blue-300">
                    <CardContent className="p-4" onClick={() => handleSelectCustomer(customer)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{customer.name}</h4>
                            <p className="text-sm text-gray-600">{customer.email}</p>
                            {customer.phone && (
                              <p className="text-sm text-gray-500">{customer.phone}</p>
                            )}
                            {address && (
                              <p className="text-sm text-gray-500 mt-1">
                                {address.line1 ? `${address.line1}, ${address.city}, ${address.state}` : 
                                 address.city ? `${address.city}, ${address.state}` : 'No address'}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          <div className="text-gray-500">Orders: <span className="font-medium">{customer.orderHistory?.length || 0}</span></div>
                          <div className="text-gray-500">Spent: <span className="font-medium">${customer.totalSpent?.toFixed(2) || '0.00'}</span></div>
                          <div className="text-gray-500">Addresses: <span className="font-medium">{(customer.addresses?.length || 0) + (customer.address ? 1 : 0)}</span></div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {searchQuery.trim().length < 2 && (
            <div className="text-center py-8 text-gray-500">
              <User className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p>Start typing to search for existing customers</p>
              <p className="text-sm mt-1">Search by name, email, or phone number</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Product Selection Step Component
interface ProductSelectionStepProps {
  orderItems: OrderItem[];
  onItemsChange: (items: OrderItem[]) => void;
}

interface ProductWithQuantity extends Product {
  selectedQuantity: number;
}

function ProductSelectionStep({ orderItems, onItemsChange }: ProductSelectionStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<ProductWithQuantity[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<ProductWithQuantity[]>([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedBrand, setSelectedBrand] = useState<string>('All');
  const [categories, setCategories] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);

  // Load initial products
  useEffect(() => {
    loadProducts();
  }, []);

  // Sync selected quantities with orderItems prop (simplified to avoid loops)
  useEffect(() => {
    if (orderItems.length === 0) return; // Skip if no items
    
    setProducts(prevProducts => 
      prevProducts.map(product => {
        const selectedItem = orderItems.find(item => item.id === product.id);
        return {
          ...product,
          selectedQuantity: selectedItem ? selectedItem.quantity : 0
        };
      })
    );
  }, [orderItems]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const result = await searchProducts('', 'All', 'All');
      
      const productsWithQuantity: ProductWithQuantity[] = result.products
        .filter(product => !product.disabled && (product.stockQuantity === undefined || product.stockQuantity > 0))
        .map(product => ({
          ...product,
          selectedQuantity: orderItems.find(item => item.id === product.id)?.quantity || 0
        }));

      setProducts(productsWithQuantity);

      // Extract unique categories and brands
      const uniqueCategories = [...new Set(result.products.map(p => p.category || 'Uncategorized').filter(Boolean))];
      const uniqueBrands = [...new Set(result.products.map(p => p.brand).filter(Boolean))];
      
      setCategories(['All', ...uniqueCategories.sort()]);
      setBrands(['All', ...uniqueBrands.sort()]);
      
    } catch (error) {
      console.error('Failed to load products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };


  // Update filtered products when search or filters change
  useEffect(() => {
    let filtered = [...products];

    // Apply category filter
    if (selectedCategory && selectedCategory !== 'All') {
      filtered = filtered.filter(product => 
        selectedCategory === 'Uncategorized' 
          ? (!product.category || product.category === 'Uncategorized')
          : product.category === selectedCategory
      );
    }

    // Apply brand filter
    if (selectedBrand && selectedBrand !== 'All') {
      filtered = filtered.filter(product => product.brand === selectedBrand);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchLower) ||
        (product.description && product.description.toLowerCase().includes(searchLower)) ||
        (product.tags && product.tags.some(tag => tag.toLowerCase().includes(searchLower)))
      );
    }

    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedCategory, selectedBrand]);

  const handleQuantityChange = (product: ProductWithQuantity, newQuantity: number) => {
    const quantity = Math.max(0, newQuantity);

    // Update local state
    setProducts(prevProducts =>
      prevProducts.map(p =>
        p.id === product.id ? { ...p, selectedQuantity: quantity } : p
      )
    );

    // Update parent component
    const updatedItems = [...orderItems];
    const existingIndex = updatedItems.findIndex(item => item.id === product.id);

    if (quantity === 0) {
      // Remove item if quantity is 0
      if (existingIndex >= 0) {
        updatedItems.splice(existingIndex, 1);
      }
    } else {
      // Add or update item
      const orderItem: OrderItem = {
        id: product.id,
        name: product.name,
        price: product.price,
        onSalePrice: product.onSalePrice,
        onSale: product.onSale,
        image: product.image,
        quantity,
        listingQuantity: product.listingQuantity
      };

      if (existingIndex >= 0) {
        updatedItems[existingIndex] = orderItem;
      } else {
        updatedItems.push(orderItem);
      }
    }

    onItemsChange(updatedItems);
  };

  const getEffectivePrice = (product: Product) => {
    return product.onSale && product.onSalePrice && product.onSalePrice < product.price
      ? product.onSalePrice
      : product.price;
  };

  const formatPrice = (price: number) => {
    return `$${price.toFixed(2)}`;
  };

  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedBrand('All');
    setSearchQuery('');
  };

  const getSubtotal = () => {
    return orderItems.reduce((total, item) => {
      const effectivePrice = item.onSale && item.onSalePrice && item.onSalePrice < item.price
        ? item.onSalePrice
        : item.price;
      return total + (effectivePrice * item.quantity);
    }, 0);
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search products by name, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant={showFilters ? "default" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Filters
            {(selectedCategory !== 'All' || selectedBrand !== 'All') && (
              <Badge className="ml-1 bg-blue-600 text-white">
                {(selectedCategory !== 'All' ? 1 : 0) + (selectedBrand !== 'All' ? 1 : 0)}
              </Badge>
            )}
          </Button>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jamaica-green/20"
                  >
                    {categories.map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Brand</Label>
                  <select
                    value={selectedBrand}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-jamaica-green/20"
                  >
                    {brands.map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                  </select>
                </div>
              </div>
              {(selectedCategory !== 'All' || selectedBrand !== 'All' || searchQuery) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <Button
                    variant="ghost"
                    onClick={clearFilters}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Selected Items Summary */}
        {orderItems.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-600">
                  {orderItems.length} items selected
                </span>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-600">
                  Total: {orderItems.reduce((sum, item) => sum + item.quantity, 0)} units
                </div>
                <div className="font-medium text-blue-600">
                  Subtotal: {formatPrice(getSubtotal())}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {orderItems.map(item => (
                <Badge key={item.id} variant="outline" className="border-blue-600 text-blue-600">
                  {item.name} × {item.quantity}
                  <button
                    onClick={() => handleQuantityChange(
                      products.find(p => p.id === item.id)!,
                      0
                    )}
                    className="ml-2 hover:text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Products Grid */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading products...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery || selectedCategory !== 'All' || selectedBrand !== 'All'
                ? 'Try adjusting your search or filter criteria.'
                : 'Start typing to search for products.'}
            </p>
            {(searchQuery || selectedCategory !== 'All' || selectedBrand !== 'All') && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredProducts.map((product, index) => {
              const effectivePrice = getEffectivePrice(product);
              const isSelected = product.selectedQuantity > 0;
              
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Card className={`transition-all duration-200 hover:shadow-md ${
                    isSelected ? 'ring-2 ring-jamaica-green border-jamaica-green' : ''
                  }`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <div className="flex-shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-lg"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/api/placeholder/64/64';
                            }}
                          />
                        </div>
                        
                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className="space-y-1">
                            <h3 className="font-medium text-gray-900 truncate" title={product.name}>
                              {product.name}
                            </h3>
                            
                            {/* Price */}
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">
                                {formatPrice(effectivePrice)}
                              </span>
                              {product.onSale && product.onSalePrice && product.onSalePrice < product.price && (
                                <>
                                  <span className="text-sm text-gray-500 line-through">
                                    {formatPrice(product.price)}
                                  </span>
                                  <Badge className="bg-red-100 text-red-800 text-xs">Sale</Badge>
                                </>
                              )}
                            </div>
                            
                            {/* Stock and badges */}
                            <div className="flex flex-wrap gap-1 text-xs">
                              {product.stockQuantity !== undefined && (
                                <span className="text-gray-500">
                                  {product.stockQuantity > 0 ? `${product.stockQuantity} in stock` : 'Out of stock'}
                                </span>
                              )}
                              {product.category && (
                                <Badge variant="outline" className="text-xs">
                                  {product.category}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Quantity Controls */}
                        <div className="flex-shrink-0">
                          <div className="flex flex-col items-center gap-2">
                            <div className="flex items-center bg-gray-100 rounded-lg">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleQuantityChange(product, product.selectedQuantity - 1)}
                                disabled={product.selectedQuantity === 0}
                                className="h-8 w-8 p-0 hover:bg-gray-200"
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              
                              <span className="w-12 text-center text-sm font-medium">
                                {product.selectedQuantity}
                              </span>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleQuantityChange(product, product.selectedQuantity + 1)}
                                disabled={product.stockQuantity !== undefined && product.selectedQuantity >= product.stockQuantity}
                                className="h-8 w-8 p-0 hover:bg-gray-200"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                            
                            {product.selectedQuantity > 0 && (
                              <div className="text-xs text-center text-jamaica-green font-medium">
                                {formatPrice(effectivePrice * product.selectedQuantity)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function CreateOrderModal({ isOpen, onClose, onOrderCreated }: CreateOrderModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // Order data state
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [selectedShippingTier, setSelectedShippingTier] = useState<string>('');
  const [priorityProcessing, setPriorityProcessing] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('USD');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates>({ USD: 1, CAD: 1.35, GBP: 0.79 });
  const [loadingExchangeRates, setLoadingExchangeRates] = useState(false);
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState<{
    isValid: boolean;
    discount?: DiscountCode;
    discountAmount?: number;
    error?: string;
  } | null>(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [shippingTiers, setShippingTiers] = useState<ShippingTier[]>([]);
  const [loadingShippingTiers, setLoadingShippingTiers] = useState(false);
  
  // DHL Express rate state
  const [dhlExpressRate, setDhlExpressRate] = useState<number | null>(null);
  const [loadingDhlRate, setLoadingDhlRate] = useState(false);
  const [dhlRateError, setDhlRateError] = useState<string | null>(null);

  // Load shipping tiers and exchange rates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadShippingTiers();
      loadExchangeRates();
    }
  }, [isOpen]);

  // Fetch DHL rate when customer and order items are available
  useEffect(() => {
    if (selectedCustomer?.selectedAddress && orderItems.length > 0) {
      fetchDhlRate();
    } else {
      // Clear DHL rate when prerequisites are not met
      setDhlExpressRate(null);
      setDhlRateError(null);
    }
  }, [selectedCustomer?.selectedAddress, orderItems]);

  const loadShippingTiers = async () => {
    try {
      setLoadingShippingTiers(true);
      const tiers = await getActiveShippingTiers();
      setShippingTiers(tiers);
      if (tiers.length > 0 && !selectedShippingTier) {
        setSelectedShippingTier(tiers[0].id);
      }
    } catch (error) {
      console.error('Failed to load shipping tiers:', error);
      toast.error('Failed to load shipping options');
    } finally {
      setLoadingShippingTiers(false);
    }
  };

  const loadExchangeRates = async () => {
    try {
      setLoadingExchangeRates(true);
      const rates = await currencyService.getExchangeRates();
      setExchangeRates(rates);
    } catch (error) {
      console.error('Failed to load exchange rates:', error);
      toast.error('Failed to load exchange rates');
    } finally {
      setLoadingExchangeRates(false);
    }
  };

  // Fetch DHL Express rate for current order
  const fetchDhlRate = async () => {
    if (!selectedCustomer?.selectedAddress || orderItems.length === 0) {
      return;
    }

    try {
      setLoadingDhlRate(true);
      setDhlRateError(null);

      // Convert order items to the format expected by the API
      const cartItems = orderItems.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        onSalePrice: item.onSalePrice,
        onSale: item.onSale || false,
        quantity: item.quantity,
        image: item.image,
        manufacturer: (item as any).manufacturer || '',
        hsCode: (item as any).hsCode || '',
        originCountry: (item as any).originCountry || 'US',
        listingQuantity: item.listingQuantity || 1
      }));

      // Convert address to the format expected by the API
      const shippingAddress = {
        value: {
          address: {
            line1: selectedCustomer.selectedAddress.line1,
            line2: selectedCustomer.selectedAddress.line2 || '',
            city: selectedCustomer.selectedAddress.city,
            state: selectedCustomer.selectedAddress.state,
            postal_code: selectedCustomer.selectedAddress.postal_code,
            country: selectedCustomer.selectedAddress.country
          }
        }
      };

      const result = await fetchDhlExpressRateForAdmin(cartItems, shippingAddress);
      
      if (result.success && result.rate !== null) {
        setDhlExpressRate(result.rate);
      } else {
        setDhlRateError(result.error || 'Failed to get DHL Express rate');
        setDhlExpressRate(null);
      }
    } catch (error) {
      console.error('Error fetching DHL rate:', error);
      setDhlRateError('Failed to get DHL Express rate');
      setDhlExpressRate(null);
    } finally {
      setLoadingDhlRate(false);
    }
  };

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    // Allow empty phone as it's optional, or validate format if provided
    if (!phone) return true;
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/; // Basic international phone format
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  };

  const validatePostalCode = (code: string, country: string): boolean => {
    if (!code) return false;
    // Basic validation - can be enhanced for specific countries
    switch (country.toUpperCase()) {
      case 'US':
        return /^\d{5}(-\d{4})?$/.test(code);
      case 'CA':
        return /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(code);
      case 'GB':
      case 'UK':
        return /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/.test(code);
      default:
        return code.length >= 3; // Basic fallback
    }
  };

  const validateCustomerSelection = (): Record<string, string> => {
    const errors: Record<string, string> = {};

    // Customer selection validation
    if (!selectedCustomer) {
      errors.customer = 'Please select a customer';
      return errors;
    }

    // Validate selected address
    if (!selectedCustomer.selectedAddress) {
      errors.address = 'Customer must have a valid shipping address';
    } else {
      const address = selectedCustomer.selectedAddress;
      
      if (!address.line1?.trim()) {
        errors.addressLine1 = 'Street address is required';
      }
      if (!address.city?.trim()) {
        errors.city = 'City is required';
      }
      if (!address.state?.trim()) {
        errors.state = 'State/Province is required';
      }
      if (!address.postal_code?.trim()) {
        errors.postalCode = 'Postal code is required';
      } else if (!validatePostalCode(address.postal_code, address.country || 'US')) {
        errors.postalCode = 'Please enter a valid postal code for the selected country';
      }
    }

    return errors;
  };

  const getCurrentStepErrors = (): Record<string, string> => {
    if (currentStep === 0) {
      return validateCustomerSelection();
    }
    return {};
  };

  const isCurrentStepValid = (): boolean => {
    return Object.keys(getCurrentStepErrors()).length === 0;
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    const orderTotal = orderItems.reduce((total, item) => {
      const effectivePrice = item.onSale && item.onSalePrice && item.onSalePrice < item.price
        ? item.onSalePrice
        : item.price;
      return total + (effectivePrice * item.quantity);
    }, 0);

    setValidatingDiscount(true);
    try {
      const result = await validateDiscountCode(
        discountCode.trim(),
        'admin-user-' + Date.now(), // Use a temporary admin user ID
        orderTotal,
        selectedCustomer?.email || 'admin@example.com'
      );

      setDiscountValidation(result);
      
      if (result.isValid && result.discount && result.discountAmount !== undefined) {
        setDiscountInfo({
          code: result.discount.code,
          amount: result.discountAmount,
          type: result.discount.type
        });
        toast.success('Discount code applied successfully!');
      } else {
        toast.error(result.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Error validating discount:', error);
      toast.error('Failed to validate discount code');
      setDiscountValidation({
        isValid: false,
        error: 'Failed to validate discount code'
      });
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setDiscountCode('');
    setDiscountInfo(null);
    setDiscountValidation(null);
    toast.success('Discount removed');
  };

  // Simple calculation functions (no memoization to avoid circular dependencies)
  const getSubtotalUSD = (): number => {
    return orderItems.reduce((total, item) => {
      const effectivePrice = item.onSale && item.onSalePrice && item.onSalePrice < item.price
        ? item.onSalePrice
        : item.price;
      return total + (effectivePrice * item.quantity);
    }, 0);
  };

  const getShippingCostUSD = (): number => {
    const selectedTier = shippingTiers.find(tier => tier.id === selectedShippingTier);
    if (!selectedTier) return 0;

    // Use DHL Express rate for express shipping if available
    if (selectedTier.id === 'express' && dhlExpressRate !== null) {
      return dhlExpressRate;
    }

    // Check for free shipping threshold
    const subtotal = getSubtotalUSD();
    if (selectedTier.freeShippingThreshold && subtotal >= selectedTier.freeShippingThreshold) {
      return 0;
    }

    // Return Firebase configured cost as fallback
    return selectedTier.cost;
  };

  const getPriorityProcessingFeeUSD = (): number => {
    return priorityProcessing ? 5.00 : 0; // $5 priority processing fee
  };

  const getDiscountAmountUSD = (): number => {
    if (!discountInfo) return 0;

    const subtotal = getSubtotalUSD();
    const shippingCost = getShippingCostUSD();

    switch (discountInfo.type) {
      case 'percentage':
        return subtotal * (discountInfo.amount / 100);
      case 'fixed':
        return Math.min(discountInfo.amount, subtotal); // Can't discount more than subtotal
      case 'free_shipping':
        return shippingCost;
      default:
        return 0;
    }
  };

  const getTotalUSD = (): number => {
    const subtotal = getSubtotalUSD();
    const shipping = getShippingCostUSD();
    const priorityFee = getPriorityProcessingFeeUSD();
    const discount = getDiscountAmountUSD();

    if (discountInfo?.type === 'free_shipping') {
      // Free shipping: discount is applied to shipping, not subtracted from total
      return subtotal + priorityFee;
    } else {
      // Percentage or fixed discount: subtracted from total
      return Math.max(0, subtotal + shipping + priorityFee - discount);
    }
  };

  // Currency conversion functions
  const convertToSelectedCurrency = (usdAmount: number): number => {
    return currencyService.convertPrice(usdAmount, selectedCurrency, exchangeRates);
  };

  const formatCurrency = (amount: number, currency: Currency = selectedCurrency): string => {
    return currencyService.formatPrice(amount, currency);
  };

  // Only memoize the final totals object using primitive dependencies
  const totals = useMemo(() => {
    const subtotalUSD = getSubtotalUSD();
    const shippingUSD = getShippingCostUSD();
    const priorityFeeUSD = getPriorityProcessingFeeUSD();
    const discountUSD = getDiscountAmountUSD();
    const totalUSD = getTotalUSD();

    return {
      usd: {
        subtotal: subtotalUSD,
        shipping: shippingUSD,
        priorityFee: priorityFeeUSD,
        discount: discountUSD,
        total: totalUSD
      },
      selected: {
        subtotal: convertToSelectedCurrency(subtotalUSD),
        shipping: convertToSelectedCurrency(shippingUSD),
        priorityFee: convertToSelectedCurrency(priorityFeeUSD),
        discount: convertToSelectedCurrency(discountUSD),
        total: convertToSelectedCurrency(totalUSD)
      }
    };
  }, [orderItems, selectedShippingTier, priorityProcessing, discountInfo, selectedCurrency, exchangeRates, shippingTiers]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(0);
      setSelectedCustomer(null);
      setOrderItems([]);
      setSelectedShippingTier('');
      setPriorityProcessing(false);
      setSelectedCurrency('USD');
      setDiscountInfo(null);
      setDiscountCode('');
      setDiscountValidation(null);
      setValidationErrors({});
      setDhlExpressRate(null);
      setDhlRateError(null);
      setLoadingDhlRate(false);
    }
  }, [isOpen]);

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 0: // Customer info
        return isCurrentStepValid();
      case 1: // Items
        return orderItems.length > 0;
      case 2: // Shipping
        return selectedShippingTier;
      case 3: // Currency & discounts
        return true; // This step is optional
      case 4: // Summary
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep === 0) {
      const errors = validateCustomerSelection();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      } else {
        setValidationErrors({});
      }
    }

    if (canProceedToNextStep() && currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateOrder = async () => {
    setIsLoading(true);
    try {
      // Calculate all costs in USD
      const subtotalUSD = getSubtotalUSD();
      const shippingCostUSD = getShippingCostUSD();
      const priorityFeeUSD = getPriorityProcessingFeeUSD();
      const discountAmountUSD = getDiscountAmountUSD();
      const totalUSD = getTotalUSD();
      const displayTotal = convertToSelectedCurrency(totalUSD);

      // Get the selected shipping tier object
      const selectedTier = shippingTiers.find(tier => tier.id === selectedShippingTier);
      if (!selectedTier) {
        toast.error('Please select a shipping tier');
        return;
      }

      if (!selectedCustomer || !selectedCustomer.selectedAddress) {
        toast.error('Please select a customer with a valid address');
        return;
      }

      // Prepare order data for creation
      const orderData: CreateOrderData = {
        customer: {
          name: selectedCustomer.name,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone,
        },
        existingCustomerId: selectedCustomer.id, // Add this to link to existing customer
        shippingAddress: {
          fullName: selectedCustomer.name,
          line1: selectedCustomer.selectedAddress.line1,
          line2: selectedCustomer.selectedAddress.line2,
          city: selectedCustomer.selectedAddress.city,
          state: selectedCustomer.selectedAddress.state,
          zipCode: selectedCustomer.selectedAddress.postal_code,
          country: selectedCustomer.selectedAddress.country,
          phoneNumber: (() => {
            // Get phone number from the specifically selected address
            let phoneNumber = null;
            
            if (selectedCustomer.selectedAddressId === 'legacy') {
              phoneNumber = selectedCustomer.phone;
            } else if (selectedCustomer.selectedAddressId) {
              const selectedAddr = selectedCustomer.addresses?.find(addr => addr.id === selectedCustomer.selectedAddressId);
              phoneNumber = selectedAddr?.phoneNumber || selectedCustomer.phone;
            } else {
              // Fallback to default address phone or customer phone
              const defaultAddress = selectedCustomer.addresses?.find(addr => addr.isDefault) || selectedCustomer.addresses?.[0];
              phoneNumber = defaultAddress?.phoneNumber || selectedCustomer.phone;
            }
            
            // Return phone number or default if none available
            return phoneNumber?.trim() || '+1-000-000-0000';
          })(),
        },
        items: orderItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          onSalePrice: item.onSalePrice,
          onSale: item.onSale || false,
          quantity: item.quantity,
          image: item.image,
          listingQuantity: item.listingQuantity || 1,
        })),
        shipping: {
          tier: selectedTier,
          cost: shippingCostUSD,
          priorityProcessing,
          priorityFee: priorityFeeUSD,
          estimatedDelivery: new Date(Date.now() + (selectedTier.deliveryTimeMax || 7) * 24 * 60 * 60 * 1000),
        },
        currency: selectedCurrency,
        exchangeRates,
        subtotalUSD,
        shippingCostUSD,
        priorityFeeUSD,
        totalUSD,
        displayTotal,
        discount: discountInfo ? {
          code: discountInfo.code,
          discountId: discountValidation.discount?.id || '',
          amount: discountInfo.amount,
          type: discountInfo.type,
          amountUSD: discountAmountUSD,
        } : undefined,
        notes: `Order created via admin portal`,
        createdBy: 'admin', // Could be replaced with actual admin user info
      };

      // Validate order data
      const validation = validateOrderData(orderData);
      if (!validation.isValid) {
        toast.error(`Validation failed: ${validation.errors[0]}`);
        return;
      }

      // Create the order
      const orderId = await createAdminOrder(orderData);
      
      toast.success(`Order created successfully! Order ID: ${orderId}`);
      onOrderCreated();
      onClose();
      
    } catch (error) {
      console.error('Failed to create order:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create order. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return isOpen ? (
          <CustomerSelectionStep
            selectedCustomer={selectedCustomer}
            onCustomerChange={setSelectedCustomer}
            validationErrors={validationErrors}
          />
        ) : null;

      case 1:
        return isOpen ? (
          <ProductSelectionStep
            orderItems={orderItems}
            onItemsChange={setOrderItems}
          />
        ) : null;

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Shipping Options Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Shipping Options</h3>
              </div>
              {loadingShippingTiers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                  <span className="ml-2 text-gray-600">Loading shipping options...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {shippingTiers.map((tier) => (
                    <label
                      key={tier.id}
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedShippingTier === tier.id
                          ? 'border-jamaica-green bg-jamaica-green/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="shipping"
                          value={tier.id}
                          checked={selectedShippingTier === tier.id}
                          onChange={(e) => setSelectedShippingTier(e.target.value)}
                          className="mr-3"
                        />
                        <div>
                          <div className="font-medium text-gray-900">{tier.name}</div>
                          <div className="text-sm text-gray-600">{tier.description}</div>
                          <div className="text-sm text-gray-500">
                            Delivery: {tier.deliveryTimeMin === tier.deliveryTimeMax 
                              ? `${tier.deliveryTimeMin} days`
                              : `${tier.deliveryTimeMin}-${tier.deliveryTimeMax} days`
                            }
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        {tier.id === 'express' && loadingDhlRate ? (
                          <div className="flex items-center">
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-2" />
                            <span className="text-sm text-gray-600">Getting rate...</span>
                          </div>
                        ) : tier.id === 'express' && dhlExpressRate !== null ? (
                          <div>
                            <div className="font-medium text-gray-900">${dhlExpressRate.toFixed(2)}</div>
                            <div className="text-xs text-green-600">Live rate</div>
                          </div>
                        ) : tier.id === 'express' && dhlRateError ? (
                          <div>
                            <div className="font-medium text-gray-900">${tier.cost.toFixed(2)}</div>
                            <div className="text-xs text-orange-600">Fallback rate</div>
                          </div>
                        ) : (
                          <div className="font-medium text-gray-900">${tier.cost.toFixed(2)}</div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Priority Processing Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 bg-gradient-to-br from-orange-600 to-amber-600 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Priority Processing</h3>
              </div>
              <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors hover:border-gray-300">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={priorityProcessing}
                    onChange={(e) => setPriorityProcessing(e.target.checked)}
                    className="mr-3"
                  />
                  <div>
                    <div className="font-medium text-gray-900 flex items-center">
                      ⚡ Priority Processing
                    </div>
                    <div className="text-sm text-gray-600">Faster order processing and handling</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-gray-900">$5.00</div>
                </div>
              </label>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Currency Selection Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-gradient-to-br from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Currency Selection</h3>
                </div>
                {loadingExchangeRates && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Updating rates...
                  </div>
                )}
              </div>
              
              {loadingExchangeRates ? (
                <div className="grid grid-cols-3 gap-3">
                  {(['USD', 'CAD', 'GBP'] as Currency[]).map((currency) => (
                    <div
                      key={currency}
                      className="p-4 border border-gray-200 rounded-lg text-center bg-gray-50"
                    >
                      <div className="font-medium text-gray-500">{currency}</div>
                      <div className="text-sm text-gray-400">
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {(['USD', 'CAD', 'GBP'] as Currency[]).map((currency) => {
                    const rate = exchangeRates[currency];
                    const isSelected = selectedCurrency === currency;
                    
                    return (
                      <button
                        key={currency}
                        onClick={() => setSelectedCurrency(currency)}
                        className={`p-4 border rounded-lg text-center transition-colors ${
                          isSelected
                            ? 'border-jamaica-green bg-jamaica-green/5 ring-2 ring-jamaica-green/20'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          {currencyService.getCurrencySymbol(currency)} {currency}
                        </div>
                        <div className="text-sm text-gray-600">
                          {currency === 'USD' ? 'Base rate' : `Rate: ${rate.toFixed(3)}`}
                        </div>
                        {isSelected && (
                          <div className="mt-2">
                            <Badge className="bg-jamaica-green text-white text-xs">Selected</Badge>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              
              {/* Conversion Preview */}
              {orderItems.length > 0 && !loadingExchangeRates && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h5 className="font-medium text-blue-900 mb-2">Price Preview</h5>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-blue-700">Subtotal in USD:</span>
                      <span className="font-medium text-blue-900">
                        ${orderItems.reduce((sum, item) => {
                          const effectivePrice = item.onSale && item.onSalePrice && item.onSalePrice < item.price
                            ? item.onSalePrice
                            : item.price;
                          return sum + (effectivePrice * item.quantity);
                        }, 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-blue-700">Subtotal in {selectedCurrency}:</span>
                      <span className="font-medium text-blue-900">
                        {currencyService.formatPrice(
                          currencyService.convertPrice(
                            orderItems.reduce((sum, item) => {
                              const effectivePrice = item.onSale && item.onSalePrice && item.onSalePrice < item.price
                                ? item.onSalePrice
                                : item.price;
                              return sum + (effectivePrice * item.quantity);
                            }, 0),
                            selectedCurrency,
                            exchangeRates
                          ),
                          selectedCurrency
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Discount Code Section */}
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900">Discount Code</h3>
              </div>
              <div className="flex gap-3">
                <Input
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value)}
                  placeholder="Enter discount code (optional)"
                  className="flex-1"
                  disabled={validatingDiscount || !!discountInfo}
                />
                <Button 
                  onClick={handleApplyDiscount}
                  disabled={validatingDiscount || !discountCode.trim() || !!discountInfo}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {validatingDiscount ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    'Apply'
                  )}
                </Button>
              </div>

              {/* Validation Error */}
              {discountValidation && !discountValidation.isValid && (
                <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="text-sm">{discountValidation.error}</span>
                </div>
              )}

              {/* Applied Discount */}
              {discountInfo && discountValidation?.isValid && (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center text-green-700">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <div>
                      <div className="font-medium">
                        Discount applied: {discountInfo.code}
                      </div>
                      <div className="text-sm">
                        {discountInfo.type === 'free_shipping' ? (
                          'Free shipping'
                        ) : discountInfo.type === 'percentage' ? (
                          `${discountValidation.discount?.value}% off`
                        ) : (
                          `$${discountInfo.amount.toFixed(2)} off`
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveDiscount}
                    className="text-green-600 hover:text-green-700 font-medium text-sm"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        );

      case 4:
        const selectedTier = shippingTiers.find(t => t.id === selectedShippingTier);
        
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            {/* Summary Header */}
            <div className="text-center py-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", bounce: 0.6 }}
              >
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              </motion.div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">Order Summary</h3>
              <p className="text-gray-600">Review your order details before creating</p>
            </div>

            <div className="space-y-6">
              {/* Customer Information */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Customer Information</h4>
                </div>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{selectedCustomer?.name}</div>
                  <div>{selectedCustomer?.email}</div>
                  {selectedCustomer?.phone && <div>{selectedCustomer.phone}</div>}
                  {selectedCustomer?.selectedAddress && (
                    <div className="mt-2">
                      <div>{selectedCustomer.selectedAddress.line1}</div>
                      {selectedCustomer.selectedAddress.line2 && <div>{selectedCustomer.selectedAddress.line2}</div>}
                      <div>
                        {selectedCustomer.selectedAddress.city}, {selectedCustomer.selectedAddress.state} {selectedCustomer.selectedAddress.postal_code}
                      </div>
                      <div>{selectedCustomer.selectedAddress.country}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Items */}
              {orderItems.length > 0 && (
                <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-8 w-8 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center">
                      <ShoppingBag className="w-4 h-4 text-white" />
                    </div>
                    <h4 className="font-semibold text-gray-900">
                      Order Items ({orderItems.length})
                    </h4>
                  </div>
                  <div className="space-y-3">
                    {orderItems.map((item, index) => {
                      const effectivePrice = item.onSale && item.onSalePrice && item.onSalePrice < item.price
                        ? item.onSalePrice
                        : item.price;
                      const itemTotal = effectivePrice * item.quantity;
                      
                      return (
                        <div key={item.id} className="flex items-center gap-3 p-2 bg-white rounded border">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-12 h-12 object-cover rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/api/placeholder/48/48';
                            }}
                          />
                          <div className="flex-1 min-w-0">
                            <h5 className="font-medium text-gray-900 truncate">{item.name}</h5>
                            <div className="text-sm text-gray-600">
                              ${effectivePrice.toFixed(2)} × {item.quantity}
                              {item.onSale && (
                                <span className="ml-2 text-red-600 text-xs">Sale</span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm font-medium text-gray-900">
                            ${itemTotal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Shipping Information */}
              <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-lg flex items-center justify-center">
                    <Truck className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Shipping & Processing</h4>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <div>
                      <div className="font-medium">{selectedTier?.name}</div>
                      <div className="text-gray-600">{selectedTier?.description}</div>
                      {selectedTier && (
                        <div className="text-gray-500">
                          Delivery: {selectedTier.deliveryTimeMin === selectedTier.deliveryTimeMax 
                            ? `${selectedTier.deliveryTimeMin} days`
                            : `${selectedTier.deliveryTimeMin}-${selectedTier.deliveryTimeMax} days`
                          }
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div>${selectedTier?.cost.toFixed(2) || '0.00'}</div>
                    </div>
                  </div>
                  {priorityProcessing && (
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <div>
                        <div className="font-medium">⚡ Priority Processing</div>
                        <div className="text-gray-600">Faster handling & shipping</div>
                      </div>
                      <div>$5.00</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="bg-white rounded-xl p-6 border-2 border-blue-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-8 w-8 bg-gradient-to-br from-orange-600 to-amber-600 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Price Breakdown</h4>
                </div>
                <div className="space-y-2 text-sm">
                  {/* USD Breakdown */}
                  <div className="text-gray-600 font-medium mb-2">USD (Base Currency)</div>
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>${totals.usd.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping ({selectedTier?.name || 'Standard'})</span>
                    <span>
                      {discountInfo?.type === 'free_shipping' ? (
                        <div className="flex items-center gap-2">
                          <span className="line-through text-gray-500">${totals.usd.shipping.toFixed(2)}</span>
                          <span className="text-green-600">FREE</span>
                        </div>
                      ) : (
                        `$${totals.usd.shipping.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  {priorityProcessing && (
                    <div className="flex justify-between">
                      <span>Priority Processing</span>
                      <span>${totals.usd.priorityFee.toFixed(2)}</span>
                    </div>
                  )}
                  {discountInfo && discountInfo.type !== 'free_shipping' && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({discountInfo.code})</span>
                      <span>-${totals.usd.discount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <Separator className="my-3" />
                  
                  {/* Selected Currency Total */}
                  {selectedCurrency !== 'USD' && (
                    <>
                      <div className="flex justify-between font-semibold text-base text-gray-900">
                        <span>Total (USD)</span>
                        <span>${totals.usd.total.toFixed(2)}</span>
                      </div>
                      <div className="text-gray-600 font-medium mb-2 mt-3">
                        {selectedCurrency} (Exchange Rate: {exchangeRates[selectedCurrency].toFixed(3)})
                      </div>
                      <div className="flex justify-between font-semibold text-lg text-blue-600">
                        <span>Total ({selectedCurrency})</span>
                        <span>{formatCurrency(totals.selected.total)}</span>
                      </div>
                    </>
                  )}
                  
                  {selectedCurrency === 'USD' && (
                    <div className="flex justify-between font-semibold text-lg text-blue-600">
                      <span>Total (USD)</span>
                      <span>${totals.usd.total.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Notes */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">Order Notes</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• This order will be created with "paid" status</li>
                  <li>• Exchange rate used: 1 USD = {exchangeRates[selectedCurrency].toFixed(3)} {selectedCurrency}</li>
                  {discountInfo && (
                    <li>• Discount "{discountInfo.code}" will be applied</li>
                  )}
                  {priorityProcessing && (
                    <li>• Priority processing will expedite fulfillment</li>
                  )}
                </ul>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <ShoppingCart className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Create New Order
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-4 text-white/80 text-sm mt-1"
                    >
                      <span>Step {currentStep + 1} of {steps.length}: {steps[currentStep].description}</span>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    disabled={isLoading}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="border-b border-gray-200 p-4 bg-white flex-shrink-0">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => {
                  const StepIcon = step.icon;
                  const isActive = index === currentStep;
                  const isCompleted = index < currentStep;
                  const canAccess = index <= currentStep || isCompleted;

                  return (
                    <React.Fragment key={step.id}>
                      <div className="flex items-center">
                        <button
                          onClick={() => canAccess && setCurrentStep(index)}
                          disabled={!canAccess || isLoading}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-blue-600 text-white'
                              : isCompleted
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                              : canAccess
                              ? 'text-gray-600 hover:bg-gray-100'
                              : 'text-gray-400'
                          } ${canAccess ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                        >
                          <StepIcon className="w-4 h-4" />
                          <span className="text-sm font-medium hidden sm:block">{step.title}</span>
                          {isCompleted && <CheckCircle className="w-3 h-3" />}
                        </button>
                      </div>
                      {index < steps.length - 1 && (
                        <div className={`flex-1 h-px mx-2 ${isCompleted ? 'bg-blue-300' : 'bg-gray-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentStep}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    {renderStepContent()}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0 || isLoading}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </Button>

                {currentStep === steps.length - 1 ? (
                  <Button
                    onClick={handleCreateOrder}
                    disabled={!canProceedToNextStep() || isLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating Order...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Create Order
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={(currentStep === 0 ? isLoading : (!canProceedToNextStep() || isLoading))}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                  >
                    Next
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}